import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
import { RootState } from 'store';
import { IDocument, IFolder } from 'interfaces';
import { fetchUserPermissions, updateRooms } from './userDataCreator';
import { setRooms, setUserAndRoomStatus } from 'store/slices/userDataSlice';
import {
  removeDownloadedFile,
  setCurrentFolder,
  setDocuments,
  setUploadingDocuments,
  setSelectedDocument,
  setViewType,
  addDocumentIdInAsyncProcessing,
  deleteDocumentIdInAsyncProcessing,
  setDocumentTimestampsCreating,
  deleteDocumentTimestampsCreating,
} from 'store/slices/dataDocumentsSlice';
import { setDataRoom, setNeedToUpdateTerms } from 'store/slices/dataRoomSlice';
import { setFoldersForPermissions } from 'store/slices/dataPermissionsSlice';
import { fetchSigningState } from './signingCreator';
import { NavigateFunction } from 'react-router';
import { i18n, TFunction } from 'i18next';
import { setDocumentToViewNull, setIsDocumentError, setViewerLoading, setViewerSlice } from 'store/slices/viewerSlice';
import { DocumentUpload } from 'interfaces/IDocument';
import getFilenameFromResponse from 'helpers/getFilenameFromResponse';
import { setRoomAllChats } from 'store/slices/chatsSlice';
import { MessageInstance } from 'antd/es/message/interface';
import { setUpdateAfterDeleteTimer } from 'store/slices/windowStateSlice';
import { setAnalysisData, setAnalysisProcessStatus } from 'store/slices/aiFunctionsDataSlice';

export type ISocketMessage = {
  timestamp: string;
  message: string;
  event: string;
  room_id?: string;
  folder_id?: string;
  parent_folder_id?: string;
  token?: string;
  success?: any;
};

type uploadPayload = {
  type: 'add' | 'replace';
  newDocuments?: IDocument[];
  websocketData?: any;
  navigate: NavigateFunction;
  roomId?: string;
  forceUpdate?: boolean;
};

type deletePayload = {
  document_id: string;
  folder_id?: string;
  room_id?: string;
  prev_version?: string;
};

type deleteFolderPayload = {
  folder_id: string;
  room_id: string;
};

type moveDocumentPayload = {
  folder_id: string;
  room_id: string;
  document_id: string;
  old_folder_id?: string
};

type updatePermissionsType = {
  room_id: string;
  folder_id: string;
  new_permissions: string[];
  group_id: string;
  author: string;
};

const add = (
  newElements: IDocument[],
  source: (IDocument | IFolder)[],
  parentFolder?: any
) => {
  const availableIds = source.map((doc) => doc.id);
  const matchingIds: (string | null | undefined)[] = [];

  const newElementsWithoutDuplicates = newElements
    ?.filter((document) => {
      if (availableIds.includes(document.id)) matchingIds.push(document.id);
      return !availableIds.includes(document.id);
    })
    .map((doc) => ({
      ...doc,
      status: 0,
      has_notes: false,
      confident_document: [],
      is_agreed: null,
      signing_status: null, // typesToSigning.includes(doc.extension || '') ? null : '',
      permissions: parentFolder.permissions,
      clicked: false,
      key: doc.id,
      agreement_status: null,
    })) as IDocument[];

  const sourceElementsWithRightPermissions = !matchingIds[0]
    ? source
    : source.map((document) =>
        !matchingIds.includes(document.id)
          ? document
          : {
              ...document,
              status: 1,
              permissions: parentFolder.permissions,
            }
      );

  return [
    ...newElementsWithoutDuplicates,
    ...sourceElementsWithRightPermissions,
  ];
};

const delayOfCreatingNew = 300;

const fetchDocumentsData = createAsyncThunk(
  'documents/fetchData',
  async ({ type, newDocuments, websocketData, navigate, roomId }: uploadPayload, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;
    const { userFoldersPermissions, userRootPermissions, foldersForPermissions } = rootState.permissions;
    const { roomAllChats } = rootState.chat;
    const { dataRoom } = rootState.dataRoom;
    const { available_rooms } = rootState.userData;
    const {
      dataDocumentTable,
      currentFolder,
      selectedDocument,
      documentIdsInAsyncProcessing,
      documents: sourceDocuments,
      folders: sourceFolders,
      documentTimestampsCreating,
    } = rootState.documents;

    const folderPath = currentFolder[currentFolder.length - 1]?.id || null;
    const parentFolderID = currentFolder[0] ? currentFolder[0]?.id : null;

    let currentFolderWithPermissions = userRootPermissions;
    if (parentFolderID) {
      const permissionsFromUsersFolders = userFoldersPermissions?.find((folder) => folder.id === parentFolderID);
      const permissionsFromFolders = foldersForPermissions?.find((folder) => folder.id === parentFolderID);
      currentFolderWithPermissions = permissionsFromUsersFolders || permissionsFromFolders;
    }

    if (roomId && roomId !== dataRoom?.id) {
      return;
    };
    let payloadDocuments: IDocument[] | undefined = undefined;
    const isTheSameRoom = websocketData?.room_id === dataRoom?.id;
    const isTheSameFolder = websocketData?.folder_id === folderPath;
    const isThisFileConfident = websocketData?.is_confidential;
    const isApprovedNDA = dataRoom?.user_status === 2;
    const canSeeThisFile = isThisFileConfident ? isApprovedNDA : true;

    if (websocketData && isTheSameRoom && isTheSameFolder && canSeeThisFile) {
      try {
        const response = await api.getDocumentById(websocketData?.document_id);
        payloadDocuments = [response.data];
        if (response.data?.status === 1 && websocketData?.document_id) {
          dispatch(
            setUploadingDocuments({
              document_id: websocketData.document_id,
              document_name: response.data.name,
              status: 'UPLOADED',
              uploadProgress: 100,
            })
          );
        }
      } catch (e) {
        return thunkApi.rejectWithValue('Failed to get document by ID');
      }
    };
    // Не блокируем обработку если есть оригинальный документ (based_on) со status = 0 (показывает лоадер)
    const basedOnDocument = sourceDocuments.find(doc => doc.id === payloadDocuments?.[0]?.based_on);
    const isBasedOnDocumentLoading = basedOnDocument?.status === 0;
    const isDocumnetAlreadyProcessing = documentIdsInAsyncProcessing.includes(websocketData?.document_id);

    if (isDocumnetAlreadyProcessing && !isBasedOnDocumentLoading) {      
      return;
    } else if (type === 'replace') {
      dispatch(addDocumentIdInAsyncProcessing(websocketData?.document_id));
      setTimeout(() => {
        dispatch(deleteDocumentIdInAsyncProcessing(websocketData?.document_id));
      }, 1000);
    };
   
    const documentsForUpdate = newDocuments || payloadDocuments;
    if (documentsForUpdate) {
      // Если документа уже не существует, а модальное окно с деталями по нему сейчас открыто - то принудительно закрываем секцию с деталями файла
      if (documentsForUpdate[0].based_on) {
        if (documentsForUpdate[0].based_on === selectedDocument?.id) {
          dispatch(setViewType('Table'));
          dispatch(setSelectedDocument(null));
          dispatch(setDocumentToViewNull());
          navigate && navigate(window.location.pathname);
        }

        const roomAllChatsIds = roomAllChats.map(chat => chat.entity_id);

        if (roomAllChatsIds.includes(documentsForUpdate[0].based_on)) {
          const chatsReplacedBasedOn = roomAllChats.map((chat) => chat.entity_id === documentsForUpdate[0].based_on
            ? {
              ...chat,
              entity_id: documentsForUpdate[0].id,
              entity_name: documentsForUpdate[0].name,
              title: documentsForUpdate[0].name,
            }
            : chat
          );
          dispatch(setRoomAllChats(chatsReplacedBasedOn));
        }
      }

      const theSameDocumentInSource = sourceDocuments.find(file => file.id === documentsForUpdate[0].id);
      const docForUpdate = documentsForUpdate[0];

      if (
        type === 'replace' && 
        theSameDocumentInSource?.status === 1 &&
        theSameDocumentInSource.signing_status === docForUpdate.signing_status && 
        theSameDocumentInSource.agreement_status === docForUpdate.agreement_status
      ) {
        return;
      } else {
        if (type === 'replace') {
          const currentTimestamp = new Date().getTime();
          const theLagestTS = [...documentTimestampsCreating].sort().reverse()[0];
          let delayCurrent = 0;
          let timestampWithDelay = 0;
  
          // console.log(documentTimestampsCreating, '<<<documentTimestampsCreating');
          // console.log(currentTimestamp - theLagestTS,' <<<<<currentTimestamp - theLagestTS');
          
          if (currentTimestamp - theLagestTS < 0) {
            timestampWithDelay = currentTimestamp + (delayOfCreatingNew + (theLagestTS - currentTimestamp));
            delayCurrent = delayOfCreatingNew + (theLagestTS - currentTimestamp);
          } else if (currentTimestamp - theLagestTS < delayOfCreatingNew) {
            timestampWithDelay = currentTimestamp + delayOfCreatingNew;
            delayCurrent = delayOfCreatingNew;
          } else {
            timestampWithDelay = currentTimestamp;
            delayCurrent = 0;
          };
          dispatch(setDocumentTimestampsCreating(timestampWithDelay));

          // console.log(timestampWithDelay, '<<<<timestampWithDelay');
          // console.log(delayCurrent, '<<<<delayCurrent');

          setTimeout(() => {
            dispatch(replaceDocument({
              newElement: documentsForUpdate,
              permissions: currentFolderWithPermissions,
              parentFolder: folderPath,
            }));

            setTimeout(() => {
              timestampWithDelay && dispatch(deleteDocumentTimestampsCreating(timestampWithDelay));
            }, delayOfCreatingNew);
          }, delayCurrent);

        } else {
          const documents = add(
            documentsForUpdate,
            sourceDocuments,
            currentFolderWithPermissions,
          ) as IDocument[];

          dispatch(setDocuments({
            documents,
            folders: sourceFolders,
            activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
          }));

          // increment documets count for task#18400
          
          const numberOfNewFiles = documentsForUpdate.length;
          const currentRoom = available_rooms.find(someRoom => someRoom.id === roomId);
          const updatedDocAmmaount = currentRoom!.documents_amount ? Number(currentRoom!.documents_amount) + numberOfNewFiles : numberOfNewFiles;
          const updatedNewDocsCount = currentRoom!.new_count ? Number(currentRoom!.new_count) + numberOfNewFiles : numberOfNewFiles;

          const roomPlusFile = {
            ...currentRoom!,
            documents_amount: updatedDocAmmaount,
            new_count: updatedNewDocsCount,
          };
          
          dataRoom && dataRoom.id === roomId && dispatch(setDataRoom({...dataRoom, documents_amount: updatedDocAmmaount, new_count: updatedNewDocsCount}));
          dispatch(setRooms(available_rooms.map((someRoom) =>
            someRoom.id === currentRoom?.id
              ? roomPlusFile
              : someRoom
          )));
        }
      }
    }
  }
);

type IReplaceProps = {
  newElement: IDocument[];
  permissions?: any;
  parentFolder?: string | null;
}

const replaceDocument = createAsyncThunk(
  'replaceDocument/replace',
  async ({newElement, permissions, parentFolder}: IReplaceProps, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;

    const { documents, folders, dataDocumentTable } = rootState.documents;    

    const isHasDocument = Boolean(
      documents.find(({ id }) => id === newElement[0].id)
    );
    const isBasedOn = newElement[0]?.based_on;
    const isTheSameFolder = parentFolder === newElement[0].folder_id;

    const resultDocsArr = isTheSameFolder && !isHasDocument
      ? [
          ...documents,
          { ...newElement[0], permissions: permissions?.permissions },
        ].filter(file => file.id !== isBasedOn)
      : documents.map((sourceDocument) =>
        sourceDocument.id === newElement[0].id
          ? {
            ...sourceDocument,
            key: newElement[0].token,
            upload_at: newElement[0].upload_at,
            is_confidential: newElement[0].is_confidential,
            status: newElement[0].status,
            signing_status: newElement[0].signing_status,
            agreement_status: newElement[0].agreement_status,
            token: newElement[0].token,
            is_signed: newElement[0].is_signed,
            permissions: permissions?.permissions,
            version_count: newElement[0].version_count,
            is_new: newElement[0].is_new,
            message_amount: newElement[0]?.message_amount,
            unviewed_amount: newElement[0]?.unviewed_amount,
          }
        : sourceDocument
      ).filter(file => file.id !== isBasedOn);

    // console.log(resultDocsArr,'<<<<resultDocsArr');
    dispatch(setDocuments({
      documents: resultDocsArr,
      folders,
      activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
    }));
  }
);

const addNewFolder = createAsyncThunk(
  'addFolder/fetchData',
  async (newFolder: IFolder, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;
    const { foldersForPermissions } = rootState.permissions;
    const { dataRoom } = rootState.dataRoom;
    const {
      currentFolder,
      documents: sourceDocuments,
      folders: sourceFolders,
    } = rootState.documents;
    const folderPath = currentFolder[currentFolder.length - 1]?.id || null;

    const folders = sourceFolders
      .map((folder) => folder.id)
      .includes(newFolder.id)
      ? sourceFolders
      : [...sourceFolders, newFolder];
    dispatch(setDocuments({ documents: sourceDocuments, folders }));
    dispatch(setDataRoom(dataRoom!));

    folderPath === null && !foldersForPermissions.map((folder) => folder.id).includes(newFolder.id) &&
      dispatch(setFoldersForPermissions([...foldersForPermissions, newFolder]));
  }
);

const deleteThisDocument = createAsyncThunk(
  'document_delete/ws_message',
  async (payload: deletePayload, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { documents, currentFolder, folders, dataDocumentTable } = state.documents;
      const { updateAfterDeleteTimer } = state.windowState;

      if (updateAfterDeleteTimer) window.clearInterval(updateAfterDeleteTimer);      
      dispatch(setUpdateAfterDeleteTimer(setTimeout(() => {
        dispatch(updateRooms());
      }, 3000)));

      const folderPath = currentFolder.length - 1;

      if (
        (!currentFolder[folderPath] && !payload.folder_id) ||
        payload.folder_id === currentFolder[folderPath].id
      ) {
        const withoutDeletedDocuments = documents.filter(
          (doc) => doc.id !== payload.document_id
        );
        dispatch(setDocuments({
          documents: withoutDeletedDocuments,
          folders,
          activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
        }));
        return withoutDeletedDocuments;
      }

      return null;
    } catch (err) {
      return thunkApi.rejectWithValue('Failed to delete document');
    }
  }
);

const deleteThisFolder = createAsyncThunk(
  'folder_delete/ws_message',
  async (payload: deleteFolderPayload, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { documents, folders, dataDocumentTable } = state.documents;
      const { dataRoom } = state.dataRoom;
      const { foldersForPermissions } = state.permissions;
      const { updateAfterDeleteTimer } = state.windowState;

      if (updateAfterDeleteTimer) window.clearInterval(updateAfterDeleteTimer);
      dispatch(setUpdateAfterDeleteTimer(setTimeout(() => {
        dispatch(updateRooms());
      }, 3000)));

      dispatch(
        setDocuments({
          documents,
          folders: folders.filter(({ id }) => id !== payload.folder_id),
          activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
        })
      );
      dispatch(
        setDataRoom({
          ...dataRoom!,
          folders: folders.filter(({ id }) => id !== payload.folder_id),
        })
      );
      dispatch(
        setFoldersForPermissions(
          foldersForPermissions.filter(({ id }) => id !== payload.folder_id)
        )
      );

      return null;
    } catch (err) {
      return thunkApi.rejectWithValue('Failed to delete document');
    }
  }
);

const fetchDocumentUpdate = createAsyncThunk(
  'document_update/ws_message',
  async (payload: deletePayload, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { roomAllChats } = state.chat;
    const { documents, folders, selectedDocument, dataDocumentTable } = state.documents;
    const documentPermissions = dataDocumentTable.find(
      ({ id }) => payload.document_id
    )?.permissions!;

    if (payload.document_id) {
      dispatch(addDocumentIdInAsyncProcessing(payload.document_id));
    }

    try {
      const response = await api.getDocumentById(payload.document_id);
      /// functionality fo document_replace
      const allDocIds = documents.map(doc => doc.id);
      const isContainPrevId = allDocIds.includes(String(payload.prev_version));
      const updating_document_id = (payload.prev_version && isContainPrevId)
        ? payload.prev_version
        : payload.document_id;      

      dispatch(
        setDocuments({
          documents: documents.map((el) =>
            el.id === updating_document_id
              ? { ...response.data, permissions: documentPermissions, message_amount: response.data.message_amount }
              : el
          ),
          folders,
          activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
        })
      );

      const targetChat = roomAllChats.find(chat => chat.entity_id === payload.prev_version);

      if (targetChat) {
        const chatsReplacedBasedOn = roomAllChats.map((chat) => chat.entity_id === payload.prev_version
          ? {
            ...chat,
            entity_id: payload.document_id,
            entity_name: response.data.name,
            title: response.data.name,
          }
          : chat
        );
        dispatch(setRoomAllChats(chatsReplacedBasedOn));
        // Для удаления сообщение в подменяемом чате, чтобы при переходе в него подтянуть новые сообщение
        // const chatDataStore = JSON.parse(JSON.stringify(chatData)) as typeof chatData;
        // const chatId = targetChat.id || '';
        // if (chatDataStore[chatId]) chatDataStore[chatId].data = [];
        // dispatch(setChatData(chatDataStore));
      }

      selectedDocument?.id === response.data.id &&
        dispatch(setSelectedDocument({ ...response.data, permissions: documentPermissions, message_amount: selectedDocument.message_amount }));

      if (selectedDocument?.id === response.data.prev_version) {
        dispatch(setViewerLoading());
        dispatch(setSelectedDocument(response.data));
        // сброс результатов анализа
        dispatch(setAnalysisData(null));
        dispatch(setAnalysisProcessStatus('checking'));
      };
      return null;
    } catch (err) {
      return thunkApi.rejectWithValue('Failed to delete document');
    } finally {
      if (payload.document_id) {
        setTimeout(() => {
          dispatch(deleteDocumentIdInAsyncProcessing(payload.document_id));
        }, 1000);
      }
    }
  }
);

const updateDocumentSign = createAsyncThunk(
  'document_signing/update',
  async (payload: IDocument, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { documents, folders, dataDocumentTable } = state.documents;
    const documentPermissions = dataDocumentTable.find(({ id }) => payload.id)
      ?.permissions!;
    dispatch(
      setDocuments({
        documents: documents.map((el) =>
          el.id === payload.id
            ? { ...payload, permissions: documentPermissions }
            : el
        ),
        folders,
      })
    );
    // selectedDocument?.id === payload.id && dispatch(setSelectedDocument(payload));
    return null;
  }
);

const fetchDocumentMove = createAsyncThunk('document_move/ws_message', async (payload: moveDocumentPayload, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { documents, folders, currentFolder } = state.documents;
    const folderPath = currentFolder[currentFolder.length - 1]?.id || null;
    const payloadFolderId = payload.folder_id === 'None' ? null : payload.folder_id;
    const payloadOldFolderId = payload.old_folder_id === 'None' ? null : payload.old_folder_id;

    if (payloadFolderId === folderPath) {
      const response = await api.getDocumentById(payload.document_id);
      const isHasDocument = Boolean(documents.find(({ id }) => id === response.data.id));      
      !isHasDocument && dispatch(setDocuments({ documents: [...documents, response.data], folders }));
    } else if (payloadOldFolderId === folderPath) {      
      const withoutMovedDocuments = documents.filter((doc) => doc.id !== payload.document_id);
      dispatch(setDocuments({ documents: withoutMovedDocuments, folders }));
    }
    
    return null;
  } catch (err) {
    return thunkApi.rejectWithValue('Failed to moved document');
  }
});

const fetchFoldersPermissions = createAsyncThunk(
  'permissions_update/ws_message',
  async (payload: updatePermissionsType, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { currentFolder } = state.documents;
      const { userData } = state.userData;
      const { dataRoom } = state.dataRoom;

      if (userData?.email === payload.author) return null;

      if (
        payload.folder_id === null ||
        currentFolder.find(({ id }) => id === payload.folder_id)
      ) {
        localStorage.removeItem('previousFolderPath');
        dispatch(setCurrentFolder([]));
        dispatch(fetchUserPermissions({ idRoom: dataRoom?.id! }));
        dispatch(setUserAndRoomStatus('fulfilledDataRoom'));
        return null;
      }

      return null;
    } catch (err) {
      return thunkApi.rejectWithValue('Failed to update permissions');
    }
  }
);

const updateSigningState = createAsyncThunk(
  'signing_state/update',
  async (payload: any, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { selectedDocument } = state.documents;
      // Если id документа, модальное окно с деталями по которому сейчас открыто (selectedDocument.id)
      // совпадает с payload.document_id - то обновляем стейт подписания
      if (
        selectedDocument &&
        selectedDocument?.signing_status !== null &&
        payload.document_id === selectedDocument?.id
      ) {
        dispatch(fetchSigningState({ id: selectedDocument?.id! }));
      }
    } catch (err) {
      return thunkApi.rejectWithValue('Failed to update signing');
    }
  }
);

const fetchNewFolder = createAsyncThunk(
  'folder_create/ws_message',
  async (payload: ISocketMessage, thunkApi) => {
    const rootState = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { dataRoom } = rootState.dataRoom;
    const { foldersForPermissions } = rootState.permissions;
    const {
      currentFolder,
      documents: sourceDocuments,
      folders: sourceFolders,
    } = rootState.documents;

    const folderPath = currentFolder[currentFolder.length - 1]?.id || null;
    const isTheSameRoom = payload?.room_id === dataRoom?.id;
    const parentFolderID = currentFolder[0] ? currentFolder[0]?.id : null;
    const prepared_parent_folder_id =
      payload.parent_folder_id === 'None' ? null : payload.parent_folder_id;
    const isTheSameFolder = prepared_parent_folder_id === parentFolderID;
    const neededParams = payload.folder_id && payload.room_id;

    try {
      if (isTheSameRoom && isTheSameFolder && neededParams) {
        const newFolder = await api.getFolderOfFolder(
          payload.folder_id!,
          payload.room_id!
        );
        const folders = sourceFolders
          .map((folder) => folder.id)
          .includes(newFolder.data.id)
          ? sourceFolders
          : [...sourceFolders, newFolder.data];
        dispatch(setDataRoom({ ...dataRoom!, folders }));
        dispatch(setDocuments({ documents: sourceDocuments, folders }));
        
        folderPath === null && !foldersForPermissions.map((folder) => folder.id).includes(newFolder.data.id) &&
          dispatch(setFoldersForPermissions([ ...foldersForPermissions, newFolder.data ]));
      }
    } catch (err) {
      return thunkApi.rejectWithValue(
        'fetchNewFolder / Failed to fetch new folder'
      );
    }
  }
);

const fetchUpdatedRoom = createAsyncThunk(
  'agreement_update/ws_message',
  async (
    payload: {
      socketData: ISocketMessage;
      navigate: NavigateFunction;
      t: TFunction;
    },
    thunkApi
  ) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { dataRoom } = state.dataRoom;
      const { available_rooms } = state.userData;
      const { messageApi } = state.windowState;

      dispatch(setNeedToUpdateTerms(true));
      
      const room = await api.getRoom(payload.socketData.room_id!);
    
      const newRooms = available_rooms.map((some_room) =>
        some_room.id === room.data.id
          ? {...room.data,
            created_at: Date.parse(String(room.data.created_at)),
            last_action_timestamp: Date.parse(String(room.data.last_action_timestamp || room.data.created_at)),
            documents_amount: some_room.documents_amount,
            new_count: some_room.new_count,
            unviewed_amount: some_room.unviewed_amount,
            is_admin: some_room.is_admin,
          }
          : some_room
      ).sort((a,b) => b.last_action_timestamp - a.last_action_timestamp);
      dispatch(setRooms(newRooms || []));

      if (room.data.id === dataRoom?.id) {
        dispatch(setDataRoom({ ...room.data, need_to_update_terms: true }));

        if (
          room.data?.user_status === 1 ||
          room.data?.access_terms_status === 'waiting_for_approve'
        ) {
          payload.navigate(`/room/${room.data?.id}/nda`);
          messageApi?.warning({ content: payload.t('Documents.accessChanged'), duration: 10, key: 'accessChanged' });
        }
      }
    } catch (err) {
      return thunkApi.rejectWithValue(
        'fetchUpdatedRoom / Failed to update room'
      );
    }
  }
);

const updateDocumentUpload = createAsyncThunk(
  'document_upload/ws_message',
  async (payload: DocumentUpload, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { uploadingDocuments, canceledUploads } = state.documents;

      if (
        payload.document_id &&
        canceledUploads.includes(payload.document_id)
      ) {
        return null;
      }
      
      const tempItem = uploadingDocuments.find(
        (item) =>
          (item.document_id?.startsWith('temp_') ||
            item.old_document_id?.startsWith('temp_')) &&
          item.document_name === payload.document_name
      );

      const enhancedPayload = tempItem
        ? { ...payload, old_document_id: tempItem.document_id }
        : payload;

      const foundItem =
        uploadingDocuments.find(
          (item) => item.document_id === payload.document_id
        ) || tempItem;

      if (foundItem) {
        dispatch(setUploadingDocuments(enhancedPayload));
      }
    } catch (err) {
      return thunkApi.rejectWithValue(
        'updateDocumentUpload / Failed to update document upload'
      );
    }
  }
);

const fetchConvertedFile = createAsyncThunk('document_ready/ws_message', async (
  payload: {
    data: ISocketMessage,
    translation: { i18n: i18n, t: TFunction },
    getDownloadMessage: (filename: string, success: boolean, t: TFunction, messageApi: MessageInstance) => void},
  thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { documentUrl, documentToView } = state.viewer;
    const { i18n, t } = payload.translation;
  try {
    const { dataRoom } = state.dataRoom;
    const { filesForDownloading } = state.documents;
    const { messageApi } = state.windowState;
    
    const locale = i18n.language || 'en';
    const tokensForDownloading = filesForDownloading.map(file => file?.token || '');
    const docToken = payload?.data?.token || '';    
    const isTheSameToken = documentToView?.token === docToken;
    
    if ((isTheSameToken || documentToView?.is_temp_links) && !documentUrl) {
      if (payload?.data?.success === false) {
        dispatch(setIsDocumentError(`${t('Documents.error.gettingFailed')}: ${documentToView!.name}`));
        return;
      };      
      const fileResponse = await api.getConvertedFile({
        is_webview: true,
        locale: locale,
        token: documentToView!.token,
        extension: documentToView!.extension,
        is_temp_links: documentToView?.is_temp_links,
        is_agreement: documentToView!.is_agreement || false,
        id: dataRoom?.id,
        entity: documentToView!.id,
        action: documentToView!.is_confidential ? 'view_confident': 'view',
      });
      const filename = getFilenameFromResponse(fileResponse);  
      const fileURL = window.URL.createObjectURL(fileResponse.data as Blob);

      dispatch(setViewerSlice({
        fileURL,
        filename: filename === 'undefined' ? '' : filename,
        extension: documentToView!.extension!
      }));
    }

    if (tokensForDownloading.includes(docToken)) {
      const targetFile = filesForDownloading.find(file => file.token === docToken)
      targetFile && dispatch(removeDownloadedFile(targetFile.token));

      if (targetFile) {
        try {
          const fileResponse = await api.getConvertedFile({...targetFile, locale: locale});
          const url = window.URL.createObjectURL(fileResponse?.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = targetFile?.name!;
          document.body.appendChild(a);
          a.click();
          a.remove();
          payload.getDownloadMessage(targetFile?.name!, true, t, messageApi!)
        } catch (e) {
          dispatch(removeDownloadedFile(targetFile.token));
          payload.getDownloadMessage(targetFile?.name!, false, t, messageApi!)
        };
      }
    }
  } catch (err) {
    dispatch(setIsDocumentError(`${t('Documents.error.gettingFailed')}: ${documentToView?.name}`));
  }
});

const updateVersion = createAsyncThunk(
  'version_update/action',
  async (payload: {data: any, entityId: string}, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState;
      const { dispatch } = thunkApi;
      const { documents, folders } = state.documents;

      const isFileExist = documents.find(doc => doc.id === payload.data.id);

      !Boolean(isFileExist) && dispatch(setDocuments({
        documents: documents.map(document => {
          return document.id === payload.entityId
            ? { 
                ...document, 
                status: 0, 
                name: payload.data.name, 
                id: payload.data.id, 
                extension: payload.data.extension,
                message_amount: document.message_amount,
                unviewed_amount: document.unviewed_amount,
              }
            : document
        }),
        folders
      }));

    } catch (err) {
      return thunkApi.rejectWithValue(
        'Failed to update document version'
      );
    }
  }
);

type DocumentSigningPayload = {
  room_id: string;
  document_id: string;
  status: 'READY' | 'CONVERTING' | 'FAILED';
};

const handleDocumentSigning = createAsyncThunk(
  'document_signing/ws_message',
  async (payload: DocumentSigningPayload, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { documents, folders, dataDocumentTable } = state.documents;
    const { dataRoom } = state.dataRoom;

    if (payload.room_id !== dataRoom?.id) {
      return null;
    }

    const targetDocument = documents.find(doc => doc.id === payload.document_id);

    if (!targetDocument) {
      return null;
    }

    try {
      switch (payload.status) {
        case 'CONVERTING':
          dispatch(
            setDocuments({
              documents: documents.map((doc) =>
                doc.id === payload.document_id
                  ? { ...doc, status: 0 }
                  : doc
              ),
              folders,
              activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
            })
          );
          break;

        case 'FAILED':
          try {
            const response = await api.getDocumentById(payload.document_id);
            dispatch(
              setDocuments({
                documents: documents.map((doc) =>
                  doc.id === payload.document_id
                    ? { ...response.data, status: 1, permissions: doc.permissions }
                    : doc
                ),
                folders,
                activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
              })
            );
          } catch (error) {
            console.error('Failed to fetch original document:', error);
            dispatch(
              setDocuments({
                documents: documents.map((doc) =>
                  doc.id === payload.document_id
                    ? { ...doc, status: 1 }
                    : doc
                ),
                folders,
                activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
              })
            );
          }
          break;

        case 'READY':
          break;

        default:
          console.warn('Unknown document signing status:', payload.status);
      }

      return null;
    } catch (err) {
      return thunkApi.rejectWithValue('Failed to handle document signing');
    }
  }
);

type moveFolderPayload = {
  folder_id: string;
  target_folder_id: string | null;
  room_id: string;
  old_parent_folder_id?: string | null;
};

const fetchFolderMove = createAsyncThunk('folder_move/ws_message', async (payload: moveFolderPayload, thunkApi) => {
  try {
    const state = thunkApi.getState() as RootState;
    const { dispatch } = thunkApi;
    const { documents, folders, currentFolder, dataDocumentTable } = state.documents;
    const { dataRoom } = state.dataRoom;
    const { foldersForPermissions } = state.permissions;

    if (payload.room_id !== dataRoom?.id) return null;

    const folderPath = currentFolder[currentFolder.length - 1]?.id || null;
    const targetFolderId = payload.target_folder_id === 'None' ? null : payload.target_folder_id;
    const oldParentFolderId = payload.old_parent_folder_id === 'None' ? null : payload.old_parent_folder_id;

    if (targetFolderId === folderPath) {
      const newFolder = await api.getFolderOfFolder(payload.folder_id, payload.room_id);
      const alreadyExists = folders.some(f => f.id === newFolder.data.id);
      if (!alreadyExists) {
        dispatch(setDocuments({
          documents,
          folders: [...folders, newFolder.data],
          activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
        }));
        if (folderPath === null) {
          dispatch(setFoldersForPermissions([...foldersForPermissions, newFolder.data]));
        }
      }
    } else if (oldParentFolderId === folderPath) {
      dispatch(setDocuments({
        documents,
        folders: folders.filter(f => f.id !== payload.folder_id),
        activeMenu: dataDocumentTable.find(el => el.isOpenContextMenu),
      }));
      if (folderPath === null) {
        dispatch(setFoldersForPermissions(foldersForPermissions.filter(f => f.id !== payload.folder_id)));
      }
    }

    return null;
  } catch (err) {
    return thunkApi.rejectWithValue('Failed to handle folder move');
  }
});

export default fetchDocumentsData;
export {
  addNewFolder,
  deleteThisDocument,
  deleteThisFolder,
  fetchDocumentUpdate,
  updateDocumentSign,
  fetchDocumentMove,
  fetchFolderMove,
  fetchFoldersPermissions,
  updateSigningState,
  fetchNewFolder,
  fetchUpdatedRoom,
  fetchConvertedFile,
  updateDocumentUpload,
  updateVersion,
  handleDocumentSigning
};
